/**
 * TanStack Query hooks for ticket data.
 *
 * Stale time of 30 s — long enough to feel snappy on hover prefetches,
 * short enough that a returning user gets fresh data. The hot path
 * (triage inbox) will eventually subscribe to a Durable-Object-backed
 * stream (v2, INTERACTION_SPEC §4); for now polling on focus is fine.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ticketsApi, type CreateTimeEntryInput } from '@beacon/api-client';
import type { Ticket, TicketCreateInput, TimeEntry } from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';

export const ticketKeys = {
  all: ['tickets'] as const,
  list: () => [...ticketKeys.all, 'list'] as const,
  detail: (number: string) => [...ticketKeys.all, 'detail', number] as const,
  timeEntries: (number: string) =>
    [...ticketKeys.all, 'time-entries', number] as const,
};

export function useTickets() {
  return useQuery({
    queryKey: ticketKeys.list(),
    queryFn: async (): Promise<Ticket[]> => {
      const res = await ticketsApi.list(beaconClient);
      return res.tickets;
    },
  });
}

export function useTicket(number: string | undefined) {
  return useQuery({
    queryKey: number ? ticketKeys.detail(number) : ticketKeys.all,
    queryFn: () => ticketsApi.get(beaconClient, number!),
    enabled: Boolean(number),
  });
}

export function useTicketTimeEntries(number: string | undefined) {
  return useQuery({
    queryKey: number ? ticketKeys.timeEntries(number) : ticketKeys.all,
    queryFn: async (): Promise<TimeEntry[]> => {
      const res = await ticketsApi.listTimeEntries(beaconClient, number!);
      return res.entries;
    },
    enabled: Boolean(number),
    // Time entries sync from Harvest hourly — minute-stale is plenty,
    // and avoids hammering on every ticket detail visit.
    staleTime: 60_000,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TicketCreateInput): Promise<Ticket> =>
      ticketsApi.create(beaconClient, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.list() });
    },
    onError: (err: Error) => {
      toast.error("Couldn't create ticket", { description: err.message });
    },
  });
}

export function useCreateTicketTimeEntry(number: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTimeEntryInput): Promise<TimeEntry> =>
      ticketsApi.createTimeEntry(beaconClient, number, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.timeEntries(number) });
    },
    onError: (err: Error) => {
      toast.error("Couldn't log time", { description: err.message });
    },
  });
}
